import {patchState, signalStore, withMethods, withState} from '@ngrx/signals';
import {inject} from '@angular/core';
import {OrderClient, OrderOrderOptionEnum} from '../../../core/services/service-clients';
import {MessageService} from 'primeng/api';
import {firstValueFrom} from 'rxjs';
import {OrdersAdminState} from './models/orders-admin.model';

const initialState: OrdersAdminState = {
  orders: [],
  totalRecords: 0,
  loading: false,
  lastParams: {
    search: null,
    skip: null,
    take: null,
    sort: null,
    orderByDescending: null,
  },
};

export const adminOrdersStore = signalStore(
  withState<OrdersAdminState>(initialState),
  withMethods((store) => {
    const orderClient = inject(OrderClient);
    const messageService = inject(MessageService);

    return {
      async loadOrders(
        search: string | null = null,
        skip: number = 0,
        take: number = 10,
        orderOption: OrderOrderOptionEnum = OrderOrderOptionEnum.Created,
        orderByDescending: boolean = true
      ): Promise<void> {
        patchState(store, {loading: true});

        try {
          const [orders, totalCount] = await Promise.all([
            firstValueFrom(
              orderClient.getAllOrders(search ?? undefined, skip, take, orderOption, orderByDescending)
            ),
            firstValueFrom(
              orderClient.getAllOrdersCount(search ?? undefined)
            )
          ]);

          patchState(store, {
            orders,
            totalRecords: totalCount,
            loading: false,
            lastParams: {
              search,
              skip,
              take,
              sort: OrderOrderOptionEnum[orderOption],
              orderByDescending,
            },
          });
        } catch (error) {
          console.error('Error loading orders:', error);
          messageService.add({
            severity: 'error',
            summary: 'Fehler',
            detail: 'Bestellungen konnten nicht geladen werden'
          });
          patchState(store, {loading: false});
        }
      },

      async deleteOrder(id: string): Promise<boolean> {
        patchState(store, {loading: true});

        try {
          await firstValueFrom(orderClient.deleteOrder(id));

          const currentOrders = store.orders();
          const filteredOrders = currentOrders.filter(order => order.id !== id);
          const lastParams = store.lastParams();
          const updatedTotalCount = await firstValueFrom(
            orderClient.getAllOrdersCount(lastParams.search ?? undefined)
          );

          patchState(store, {
            orders: filteredOrders,
            totalRecords: updatedTotalCount,
            loading: false
          });

          messageService.add({
            severity: 'success',
            summary: 'Erfolgreich',
            detail: 'Bestellung wurde erfolgreich gelöscht'
          });

          return true;
        } catch (error) {
          console.error('Error deleting order:', error);
          messageService.add({
            severity: 'error',
            summary: 'Fehler',
            detail: 'Bestellung konnte nicht gelöscht werden'
          });
          patchState(store, {loading: false});
          return false;
        }
      }
    };
  })
);
